/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Box,
  Typography,
  Checkbox,
  Chip,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Response,
  Subtype,
  CourseLevel,
  SuccessResponse,
} from '../../types/ClassTable.type';
import { apiGet, apiPost } from '../../utils/apiClient';
import { notifyAPIClient } from '../../utils/toast';
import { subtleFieldBg } from '../../utils/rowHighlight';
import { getDefaultClassCapacity } from '../../utils/classCapacity';
import { ManagementTableHeaderCell } from '../common/ManagementTable';
import { useTranslation } from '../../i18n';
import { z } from 'zod';

interface ClassInputData {
  name: string;
  classDate: string;
  startTime: string;
  endTime: string;
  level: string;
  subtypeId: number;
  maxStudents: number;
  note: string;
}

const ClassInputSchema = z.object({
  name: z.string().min(1, 'Class name is required'),
  classDate: z
    .string({
      required_error: 'Please enter class date',
    })
    .date('Please enter a valid date'),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().min(1, 'End time is required'),
  level: z.string().optional(),
  subtypeId: z.number().min(1, 'Subtype is required'),
  maxStudents: z.number().int().min(1, 'Maximum students must be at least 1'),
  note: z.string().optional(),
});

type QuickAddClassDialogProps = {
  open: boolean;
  handleClose: () => void;
  fetchClasses: () => void;
  subtypes?: Subtype[];
};

// `label` is the English day name used when auto-building class names (data sent
// to the backend — must stay stable/English). `labelKey` is for display only.
const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday', labelKey: 'classQuickAdd.dayMon' },
  { value: 2, label: 'Tuesday', labelKey: 'classQuickAdd.dayTue' },
  { value: 3, label: 'Wednesday', labelKey: 'classQuickAdd.dayWed' },
  { value: 4, label: 'Thursday', labelKey: 'classQuickAdd.dayThu' },
  { value: 5, label: 'Friday', labelKey: 'classQuickAdd.dayFri' },
  { value: 6, label: 'Saturday', labelKey: 'classQuickAdd.daySat' },
  { value: 0, label: 'Sunday', labelKey: 'classQuickAdd.daySun' },
];

const formatIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateKey = (dateKey: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  date.setHours(0, 0, 0, 0);

  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return null;
  }

  return date;
};

export const getMondayDate = (date: Date): Date => {
  const monday = new Date(date);
  const currentDay = monday.getDay();
  const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1;
  monday.setDate(monday.getDate() - daysFromMonday);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

export const getDateForDayFromWeekStart = (
  weekStartDate: Date,
  dayValue: number,
): Date => {
  const monday = getMondayDate(weekStartDate);
  const daysFromMondayToTarget = dayValue === 0 ? 6 : dayValue - 1;
  const targetDate = new Date(monday);
  targetDate.setDate(monday.getDate() + daysFromMondayToTarget);
  targetDate.setHours(0, 0, 0, 0);
  return targetDate;
};

const getCurrentWeekStart = (): string =>
  formatIsoDate(getMondayDate(new Date()));

const formatDateValue = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateKey = (dateKey: string, fallback = dateKey): string => {
  const date = parseDateKey(dateKey);
  return date ? formatDateValue(date) : fallback;
};

const QuickAddClassDialog = ({
  open,
  handleClose,
  fetchClasses,
  subtypes,
}: QuickAddClassDialogProps) => {
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [selectedWeekStart, setSelectedWeekStart] =
    useState<string>(getCurrentWeekStart);
  const [selectedSubtypeId, setSelectedSubtypeId] = useState<number>(
    subtypes && subtypes.length > 0 ? subtypes[0].id : 0,
  );
  const [generatedClasses, setGeneratedClasses] = useState<ClassInputData[]>(
    [],
  );
  const [levelOptions, setLevelOptions] = useState<CourseLevel[]>([]);
  const [classesPerDay, setClassesPerDay] = useState<{ [key: number]: number }>(
    {},
  );

  const fetchCourseLevels = async (subtypeId: number) => {
    if (subtypeId === 0) {
      setLevelOptions([]);
      return;
    }
    // Find the selected subtype and get its first subsubtype ID
    const selectedSubtype = subtypes?.find((st) => st.id === subtypeId);
    if (
      !selectedSubtype ||
      !selectedSubtype.subsubtypes ||
      selectedSubtype.subsubtypes.length === 0
    ) {
      setLevelOptions([]);
      return;
    }
    const subSubtypeId = selectedSubtype.subsubtypes[0].id;
    const res = await apiGet<Response<CourseLevel>>(
      `/course/get-levels/${subSubtypeId}`,
    );
    setLevelOptions(res.data);
  };

  const handleDayToggle = (dayValue: number) => {
    setSelectedDays((prev) => {
      const isRemoving = prev.includes(dayValue);
      const newDays = isRemoving
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue].sort((a, b) => a - b);

      // Initialize classes per day for new days
      if (!isRemoving) {
        setClassesPerDay((prevCount) => ({
          ...prevCount,
          [dayValue]: 1,
        }));

        // Add new classes for the selected day, preserving existing classes
        setTimeout(() => {
          setGeneratedClasses((existingClasses) => {
            const targetDate = getDateForDay(dayValue);
            const dayAbbr = getDayAbbreviation(dayValue);
            const isoDate = formatIsoDate(targetDate);
            const subtypeName = getSelectedSubtypeName();

            const autoName = subtypeName
              ? `[${subtypeName}] - ${dayAbbr} 07:00-08:00`
              : `${dayAbbr} 07:00-08:00`;

            const newClass: ClassInputData = {
              name: autoName,
              classDate: isoDate,
              startTime: '07:00',
              endTime: '08:00',
              level: '',
              subtypeId: selectedSubtypeId,
              maxStudents: getDefaultClassCapacity(subtypeName, ''),
              note: '',
            };

            return [...existingClasses, newClass];
          });
        }, 0);
      } else {
        // Remove classes for the deselected day
        setClassesPerDay((prevCount) => {
          const newCount = { ...prevCount };
          delete newCount[dayValue];
          return newCount;
        });

        setTimeout(() => {
          setGeneratedClasses((existingClasses) => {
            const targetDate = getDateForDay(dayValue);
            const isoDate = formatIsoDate(targetDate);
            return existingClasses.filter((cls) => cls.classDate !== isoDate);
          });
        }, 0);
      }

      return newDays;
    });
  };

  const handleClassCountChange = (dayValue: number, count: number) => {
    const newCount = Math.max(1, Math.min(10, count));
    setClassesPerDay((prev) => {
      const updated = {
        ...prev,
        [dayValue]: newCount,
      };
      // Generate classes immediately with updated count
      setTimeout(() => {
        const newClasses: ClassInputData[] = [];
        const subtypeName = getSelectedSubtypeName();

        selectedDays.forEach((day) => {
          const targetDate = getDateForDay(day);
          const dayAbbr = getDayAbbreviation(day);
          const isoDate = formatIsoDate(targetDate);
          const classCount = updated[day] || 1;

          for (let i = 0; i < classCount; i++) {
            const autoName = subtypeName
              ? `[${subtypeName}] - ${dayAbbr} 07:00-08:00`
              : `${dayAbbr} 07:00-08:00`;

            newClasses.push({
              name: autoName,
              classDate: isoDate,
              startTime: '07:00',
              endTime: '08:00',
              level: '',
              subtypeId: selectedSubtypeId,
              maxStudents: getDefaultClassCapacity(subtypeName, ''),
              note: '',
            });
          }
        });

        setGeneratedClasses(newClasses);
      }, 0);

      return updated;
    });
  };

  const getDayAbbreviation = (dayValue: number): string => {
    const day = DAYS_OF_WEEK.find((d) => d.value === dayValue);
    return day ? day.label : '';
  };

  const formatDate = (date: Date): string => {
    return formatDateValue(date);
  };

  const getSelectedSubtypeName = () => {
    const selectedSubtype = subtypes?.find((st) => st.id === selectedSubtypeId);
    return selectedSubtype?.name || '';
  };

  const getDateForDay = (dayValue: number): Date => {
    const weekStartDate =
      parseDateKey(selectedWeekStart) || getMondayDate(new Date());
    return getDateForDayFromWeekStart(weekStartDate, dayValue);
  };

  const handleWeekStartChange = (date: Dayjs | null) => {
    if (!date || !date.isValid()) {
      return;
    }

    // A week always starts on Monday. Selecting any day in the calendar is
    // normalized to the Monday of that week so the table remains Monday-Sunday.
    const nextWeekStartDate = getMondayDate(date.toDate());
    const nextWeekStart = formatIsoDate(nextWeekStartDate);
    setSelectedWeekStart(nextWeekStart);

    // Keep any class details already edited by the user, moving only their
    // class dates to the newly selected week.
    setGeneratedClasses((existingClasses) =>
      existingClasses.map((cls) => {
        const currentClassDate = parseDateKey(cls.classDate);
        if (!currentClassDate) {
          return cls;
        }

        const nextClassDate = getDateForDayFromWeekStart(
          nextWeekStartDate,
          currentClassDate.getDay(),
        );
        return {
          ...cls,
          classDate: formatIsoDate(nextClassDate),
        };
      }),
    );
  };

  const generateClassesForDays = (days: number[]) => {
    if (selectedSubtypeId === 0) {
      return;
    }

    const newClasses: ClassInputData[] = [];
    const subtypeName = getSelectedSubtypeName();

    // Generate classes for selected days
    days.forEach((dayValue) => {
      const targetDate = getDateForDay(dayValue);
      const dayAbbr = getDayAbbreviation(dayValue);
      const isoDate = formatIsoDate(targetDate);

      // Get number of classes for this day
      const classCount = classesPerDay[dayValue] || 1;

      // Generate multiple classes for this day
      for (let i = 0; i < classCount; i++) {
        // Auto-generate class name with default time
        const autoName = subtypeName
          ? `[${subtypeName}] - ${dayAbbr} 07:00-08:00`
          : `${dayAbbr} 07:00-08:00`;

        newClasses.push({
          name: autoName,
          classDate: isoDate,
          startTime: '07:00',
          endTime: '08:00',
          level: '',
          subtypeId: selectedSubtypeId,
          maxStudents: getDefaultClassCapacity(subtypeName, ''),
          note: '',
        });
      }
    });

    setGeneratedClasses(newClasses);
  };

  const handleClassChange = <T extends keyof ClassInputData>(
    index: number,
    key: T,
    value: ClassInputData[T],
  ) => {
    const newList = [...generatedClasses];
    newList[index][key] = value;

    // Auto-update class name when level, startTime, or endTime changes
    if (key === 'level' || key === 'startTime' || key === 'endTime') {
      const cls = newList[index];
      const subtypeName = getSelectedSubtypeName();
      if (key === 'level') {
        cls.maxStudents = getDefaultClassCapacity(subtypeName, cls.level);
      }
      const classDate = parseDateKey(cls.classDate);
      const dayAbbr = getDayAbbreviation(classDate?.getDay() ?? 0);

      const namePrefix =
        cls.level && subtypeName ? `${subtypeName} ${cls.level}` : subtypeName;

      newList[index].name = namePrefix
        ? `[${namePrefix}] - ${dayAbbr} ${cls.startTime}-${cls.endTime}`
        : `${dayAbbr} ${cls.startTime}-${cls.endTime}`;
    }

    setGeneratedClasses(newList);
  };

  const handleSubmit = async () => {
    if (generatedClasses.length === 0) {
      notifyAPIClient(t('classQuickAdd.errNoClasses'));
      return;
    }

    // Validate all classes before submission
    const validationErrors: string[] = [];
    generatedClasses.forEach((cls, idx) => {
      if (!cls.name || cls.name.trim() === '') {
        validationErrors.push(t('classQuickAdd.errRowName', { n: idx + 1 }));
      }
      if (!cls.startTime) {
        validationErrors.push(t('classQuickAdd.errRowStart', { n: idx + 1 }));
      }
      if (!cls.endTime) {
        validationErrors.push(t('classQuickAdd.errRowEnd', { n: idx + 1 }));
      }
      if (cls.startTime && cls.endTime && cls.startTime >= cls.endTime) {
        validationErrors.push(t('classQuickAdd.errRowOrder', { n: idx + 1 }));
      }
      if (!cls.classDate) {
        validationErrors.push(t('classQuickAdd.errRowDate', { n: idx + 1 }));
      }
    });

    if (validationErrors.length > 0) {
      notifyAPIClient(
        `${t('classQuickAdd.errValidationFailed')}\n${validationErrors
          .slice(0, 3)
          .join('\n')}${
          validationErrors.length > 3
            ? `\n${t('classQuickAdd.andMoreErrors', {
                count: validationErrors.length - 3,
              })}`
            : ''
        }`,
      );
      return;
    }

    try {
      const validatedClasses = generatedClasses.map((cls) =>
        ClassInputSchema.parse(cls),
      );

      // Create classes in parallel
      const createPromises = validatedClasses.map((cls) =>
        apiPost<SuccessResponse>('/class/create', cls),
      );
      const results = await Promise.all(createPromises);

      notifyAPIClient(
        t(
          results.length === 1
            ? 'classQuickAdd.successOne'
            : 'classQuickAdd.successMany',
          { count: results.length },
        ),
      );
      fetchClasses();
      handleCloseDialog();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        const fieldName = firstError.path[0] as string;
        notifyAPIClient(
          t('classQuickAdd.errValidation', {
            field: fieldName,
            message: firstError.message,
          }),
        );
      } else {
        console.error(error);
        notifyAPIClient(t('classQuickAdd.errUnexpected'));
      }
    }
  };

  const handleCloseDialog = () => {
    setSelectedDays([]);
    setSelectedWeekStart(getCurrentWeekStart());
    setSelectedSubtypeId(subtypes && subtypes.length > 0 ? subtypes[0].id : 0);
    setGeneratedClasses([]);
    setLevelOptions([]);
    setClassesPerDay({});
    handleClose();
  };

  useEffect(() => {
    if (open) {
      if (subtypes && subtypes.length > 0) {
        const initialSubtypeId = subtypes[0].id;
        setSelectedSubtypeId(initialSubtypeId);
        fetchCourseLevels(initialSubtypeId);
      }
    }
  }, [open, subtypes]);

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={handleCloseDialog}>
      <DialogTitle>
        <Typography variant="h6" color="secondary" fontWeight="bold">
          {t('classQuickAdd.title')}
        </Typography>
        <Typography variant="body1" color="text.accent" sx={{ mt: 0.5 }}>
          {t('classQuickAdd.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Configuration Section */}
        <Card variant="outlined" sx={{ p: 3, mt: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2.5}>
            <Typography variant="h6" fontWeight={700} color="secondary.main">
              {t('classQuickAdd.configuration')}
            </Typography>
          </Box>

          {/* Week Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Week start date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(selectedWeekStart)}
                onChange={handleWeekStartChange}
                format="DD/MM/YYYY"
                label="Select week"
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { 'aria-label': 'Select week' },
                    sx: { maxWidth: 300 },
                  },
                }}
              />
            </LocalizationProvider>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.75 }}
            >
              Week: {formatDate(getDateForDay(1))} -{' '}
              {formatDate(getDateForDay(0))}
            </Typography>
          </Box>

          {/* Class Type */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600 }}
              className="required-field"
            >
              {t('classQuickAdd.classType')}
            </Typography>
            <TextField
              select
              placeholder={t('classQuickAdd.selectClassType')}
              value={selectedSubtypeId}
              onChange={(e) => {
                const newSubtypeId = Number(e.target.value);
                setSelectedSubtypeId(newSubtypeId);
                fetchCourseLevels(newSubtypeId);
                if (selectedDays.length > 0) {
                  setTimeout(() => generateClassesForDays(selectedDays), 0);
                }
              }}
              variant="outlined"
              size="small"
              sx={{ minWidth: '300px', maxWidth: '500px' }}
            >
              {subtypes?.map((subtype) => (
                <MenuItem key={subtype.id} value={subtype.id}>
                  {subtype.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Days Selection */}
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1.5}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600 }}
                className="required-field"
              >
                {t('classQuickAdd.selectDays')}
              </Typography>
              {selectedDays.length > 0 && (
                <Chip
                  size="small"
                  color="success"
                  label={t(
                    selectedDays.length === 1
                      ? 'classQuickAdd.daySelectedOne'
                      : 'classQuickAdd.daysSelectedMany',
                    { count: selectedDays.length },
                  )}
                />
              )}
            </Box>

            {/* Days-of-week table */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  {DAYS_OF_WEEK.map((day) => (
                    <ManagementTableHeaderCell key={day.value} align="center">
                      {t(day.labelKey)}
                    </ManagementTableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {DAYS_OF_WEEK.map((day) => {
                    const targetDate = getDateForDay(day.value);
                    const formattedDate = formatDate(targetDate);
                    const isSelected = selectedDays.includes(day.value);

                    return (
                      <TableCell
                        key={day.value}
                        align="center"
                        sx={(theme) => ({
                          verticalAlign: 'top',
                          backgroundColor: isSelected
                            ? alpha(theme.palette.primary.main, 0.16)
                            : 'inherit',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? alpha(theme.palette.primary.main, 0.16)
                              : alpha(theme.palette.primary.main, 0.08),
                          },
                        })}
                      >
                        <Box
                          onClick={() => handleDayToggle(day.value)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            gap: 0.5,
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleDayToggle(day.value)}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: isSelected
                                ? 'primary.main'
                                : 'text.secondary',
                              fontWeight: isSelected ? 700 : 500,
                            }}
                          >
                            {formattedDate}
                          </Typography>
                        </Box>

                        {isSelected && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.25,
                              mt: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClassCountChange(
                                    day.value,
                                    (classesPerDay[day.value] || 1) - 1,
                                  );
                                }}
                                disabled={(classesPerDay[day.value] || 1) <= 1}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography
                                sx={{
                                  minWidth: '28px',
                                  textAlign: 'center',
                                  fontWeight: 700,
                                  color: 'primary.main',
                                }}
                              >
                                {classesPerDay[day.value] || 1}
                              </Typography>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClassCountChange(
                                    day.value,
                                    (classesPerDay[day.value] || 1) + 1,
                                  );
                                }}
                                disabled={(classesPerDay[day.value] || 1) >= 10}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t(
                                classesPerDay[day.value] === 1 ||
                                  !classesPerDay[day.value]
                                  ? 'classQuickAdd.classOne'
                                  : 'classQuickAdd.classMany',
                              )}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Card>

        {/* Generated Classes Section */}
        {generatedClasses.length > 0 && (
          <Card variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2.5}
            >
              <Box display="flex" alignItems="center">
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="secondary.main"
                >
                  {t('classQuickAdd.generatedClasses')}
                </Typography>
              </Box>
              <Chip
                size="small"
                color="success"
                label={t(
                  generatedClasses.length === 1
                    ? 'classQuickAdd.classCountOne'
                    : 'classQuickAdd.classCountMany',
                  { count: generatedClasses.length },
                )}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                maxHeight: 450,
                overflowY: 'auto',
                pr: 0.5,
                alignItems: 'stretch',
              }}
            >
              {generatedClasses.map((cls, idx) => (
                <Card key={idx} variant="outlined" sx={{ p: 2, flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Chip
                      size="small"
                      color="primary"
                      label={idx + 1}
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ color: 'secondary.main', fontWeight: 700 }}
                    >
                      {t('classQuickAdd.classItem', { n: idx + 1 })}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label={t('classQuickAdd.className')}
                      type="text"
                      value={cls.name}
                      onChange={(e) =>
                        handleClassChange(idx, 'name', e.target.value)
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                    />

                    <TextField
                      select
                      label={t('classQuickAdd.level')}
                      value={cls.level}
                      onChange={(e) =>
                        handleClassChange(idx, 'level', e.target.value)
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                    >
                      {levelOptions.map((lvl, lvlIdx) => (
                        <MenuItem key={lvlIdx} value={lvl.levelName}>
                          {lvl.levelName}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Maximum Students"
                      type="number"
                      value={cls.maxStudents}
                      onChange={(e) =>
                        handleClassChange(
                          idx,
                          'maxStudents',
                          Math.max(1, Number(e.target.value) || 1),
                        )
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      inputProps={{ min: 1, step: 1 }}
                      helperText="Default by type and level"
                    />

                    <TextField
                      label={t('classQuickAdd.classDate')}
                      type="text"
                      value={formatDateKey(cls.classDate)}
                      fullWidth
                      size="small"
                      variant="outlined"
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: subtleFieldBg,
                        },
                      }}
                    />

                    <TextField
                      className="required-field"
                      label={t('classQuickAdd.startTime')}
                      type="time"
                      value={cls.startTime}
                      onChange={(e) =>
                        handleClassChange(idx, 'startTime', e.target.value)
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      error={
                        !cls.startTime ||
                        (!!cls.endTime && cls.startTime >= cls.endTime)
                          ? true
                          : false
                      }
                      helperText={
                        !cls.startTime
                          ? t('classQuickAdd.required')
                          : cls.endTime && cls.startTime >= cls.endTime
                            ? t('classQuickAdd.mustBeBeforeEnd')
                            : ''
                      }
                    />

                    <TextField
                      className="required-field"
                      label={t('classQuickAdd.endTime')}
                      type="time"
                      value={cls.endTime}
                      onChange={(e) =>
                        handleClassChange(idx, 'endTime', e.target.value)
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      error={
                        !cls.endTime ||
                        (!!cls.startTime && cls.endTime <= cls.startTime)
                          ? true
                          : false
                      }
                      helperText={
                        !cls.endTime
                          ? t('classQuickAdd.required')
                          : cls.startTime && cls.endTime <= cls.startTime
                            ? t('classQuickAdd.mustBeAfterStart')
                            : ''
                      }
                    />

                    <TextField
                      label={t('classQuickAdd.note')}
                      type="text"
                      value={cls.note}
                      onChange={(e) =>
                        handleClassChange(idx, 'note', e.target.value)
                      }
                      fullWidth
                      size="small"
                      variant="outlined"
                      sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                      multiline
                      rows={2}
                    />
                  </Box>
                </Card>
              ))}
            </Box>
          </Card>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleCloseDialog}
          variant="contained"
          color="error"
          sx={{ color: 'white' }}
        >
          {t('classQuickAdd.cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={generatedClasses.length === 0}
          variant="contained"
          color="primary"
          sx={{ color: 'white' }}
        >
          {t(
            generatedClasses.length === 1
              ? 'classQuickAdd.createOne'
              : 'classQuickAdd.createMany',
            { count: generatedClasses.length },
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddClassDialog;
